import React, { useState, useEffect, useMemo } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useUserRole } from "../../../hook/useUserRole.js";
import SelectComponent from "../../../component/SelectComponent.js";
import MainTable from "../../../component/table/MainTable.js";
import SearchableSelect from "../../../component/SearchableSelect";

const buttonStyle = {
  borderRadius: "10px",
  lineHeight: "normal",
  my: 1,
};

const AddNewMemberContent = ({ options, onCreate, onSetError, showCreate = false }) => {
  const userRoleOptions = useUserRole();
  const [addNewMemberTableData, setAddNewMemberTableData] = useState([]);
  const [addNewMemberInfo, setAddNewMemberInfo] = useState({
    id: "", //mainTable need
    memberName: "",
    email: "",
    userRole: "",
    userId: "",
    accessLevel: "",
  });

  const tableHead = [
    { id: "memberName", label: "Name", align: "left" },
    { id: "email", label: "Email", align: "left" },
    { id: "userRole", label: "User Role", align: "left" },
    { id: "accessLevelLabel", label: "Access Level", align: "left" },
    { id: "editRight", label: "Edit Right", align: "center", type: "switch" },
    {
      id: "reportModule",
      label: "Report Module",
      align: "center",
      type: "switch",
    },
    { id: "costAccess", label: "Cost Module", align: "center", type: "switch" },
  ];

  const iconColumns = [
    {
      label: "",
      width: 25,
      note: "",
      image: "assets/icons/close.svg",
      action: async (row, event) => {
        if (event) event.stopPropagation();
        if (row) {
          setAddNewMemberTableData((prevData) => prevData.filter((item) => item.userId !== row.userId));
        }
      },
    },
  ];

  const memberNameChange = async (value) => {
    const selectedUser = options.find((option) => option.userId === value);
    setAddNewMemberInfo({
      ...addNewMemberInfo,
      id: value,
      userId: value,
      // memberName: selectedUser?.firstName + " " + selectedUser?.lastName,
      memberName: selectedUser?.label,
      email: selectedUser?.email,
      userRole: selectedUser?.userRole,
      accessLevel: "",
      editRight: true, // default true for all
      reportModule: selectedUser?.userRole !== "Contractor" || addNewMemberInfo.accessLevel === 2, // default true for project admin and CLP user
      costAccess: addNewMemberInfo.accessLevel === 2, // default true for project admin
    });
  };

  const addButton = async () => {
    const isDuplicate = addNewMemberTableData.some((item) => item.userId === addNewMemberInfo.userId);
    if (isDuplicate) {
      onSetError("This member has already been assigned.");
      return;
    }
    setAddNewMemberTableData((prevData) => [
      ...prevData,
      {
        ...addNewMemberInfo,
        userRole: userRoleOptions?.find((option) => option.value === addNewMemberInfo.userRole)?.label || addNewMemberInfo.userRole,
      },
    ]);
  };

  useEffect(() => {
    setAddNewMemberTableData((prevData) => prevData?.filter((member) => options.some((option) => option.value === member.userId)));
    setAddNewMemberInfo((prevData) => ({
      ...prevData,
      userId: "",
      accessLevel: "",
      accessLevelLabel: "",
    }));
  }, [options]);

  useEffect(() => {
    if (!showCreate && onCreate) {
      onCreate(addNewMemberTableData);
    }
  }, [addNewMemberTableData, showCreate, onCreate]);

  const selectedOption = useMemo(() => {
    return options?.find((option) => option.value === addNewMemberInfo.userId) || null;
  }, [options, addNewMemberInfo.userId]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Box sx={{ display: "flex", gap: 1, minHeight: 150, maxHeight: 150 }}>
        <Typography sx={{ fontSize: "11px", minWidth: 140, fontWeight: 700 }}>Assigned Project Members: </Typography>
        <MainTable
          sx={{ height: "100%", maxHeight: "100%" }}
          columns={tableHead}
          iconColumns={iconColumns}
          rows={addNewMemberTableData}
          headerStyles={{
            default: {
              background: `#F1F1F1`,
            },
          }}
          onSwitchChange={(rowId, columnName, value) => {
            setAddNewMemberTableData((prevData) => prevData.map((row) => (row.id === rowId ? { ...row, [columnName]: value } : row)));
          }}
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
        {/* <SelectComponent
                    options={options}
                    value={addNewMemberInfor.userId}
                    onChange={memberNameChange}
                    selectLabel="Select new member:"
                    labelStyle={{ fontSize: "11px", minWidth: 140, fontWeight: 700 }}
                    selectStyle={{ width: "100%" }}
                    placeholder="Please Select the Member..."
                /> */}
        <SearchableSelect
          options={options}
          value={selectedOption}
          onChange={memberNameChange}
          label="Select new member:"
          labelStyle={{ fontSize: "11px", minWidth: 140, fontWeight: 700 }}
          textFieldStyle={{ width: "100%" }}
          placeholder="Please Select the Member..."
        />
        <SelectComponent
          options={[
            { value: 1, label: "Project Member" },
            { value: 2, label: "Project Administrator" },
          ]}
          value={addNewMemberInfo.accessLevel}
          onChange={(value, label) => {
            setAddNewMemberInfo({
              ...addNewMemberInfo,
              accessLevel: value,
              accessLevelLabel: label,
              editRight: true, // default true for all
              reportModule: addNewMemberInfo.userRole !== "Contractor" || value === 2, // default true for project admin and CLP user
              costAccess: value === 2, // default true for project admin
            });
          }}
          selectStyle={{ width: "100%" }}
          placeholder="Please Select the Access Level..."
        />
        <Button
          onClick={addButton}
          variant="contained"
          disabled={!addNewMemberInfo.userId || !addNewMemberInfo.accessLevel}
          color={!addNewMemberInfo.userId || !addNewMemberInfo.accessLevel ? "primary" : "secondary"}
          sx={buttonStyle}
        >
          Add
        </Button>
      </Box>
      {showCreate && (
        <Box textAlign="center">
          <Button
            sx={buttonStyle}
            onClick={() => onCreate(addNewMemberTableData)}
            variant="contained"
            disabled={addNewMemberTableData.length === 0}
            color={addNewMemberTableData.length === 0 ? "primary" : "success"}
          >
            Create
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default AddNewMemberContent;
